import { Injectable, Optional } from '@angular/core';
import { Level } from '@app/logger/level';

/**
 * Logger options.
 * See {@link Logger}.
 *
 * level - How much detail you want to see in the logs, 0 being off, 1 being the less detailed, 5 being the most. Defaults to WARN.
 * global - Whether you want the created logger object to be exposed in the global scope. Defaults to true.
 * globalAs - The window's property name that will hold the logger object created. Defaults to 'logger'.
 * store - Whether you want the level config to be saved in the local storage so it doesn't get lost when you refresh. Defaults to false.
 * storeAs - The local storage key that will be used to save the level config if the store setting is true. Defaults to 'angular2.logger.level'.
 *
 * Created by Langley on 3/23/2016.
 *
 */
export class Options {
  level: Level;
  global: boolean;
  globalAs: string;
  store: boolean;
  storeAs: string;
}

// For browsers that don't implement the debug method, log will be used instead. Fixes #62.
const CONSOLE_DEBUG_METHOD = console['debug'] ? 'debug' : 'log';

// Temporal until https://github.com/angular/angular/issues/7344 gets fixed.
const DEFAULT_OPTIONS: Options = {
  level: Level.WARN,
  global: true,
  globalAs: 'logger',
  store: false,
  storeAs: 'angular2.logger.level'
};

@Injectable({ providedIn: 'root' })
export class Logger {
  private _level: Level;
  private _globalAs: string;
  private _store: boolean;
  private _storeAs: string;

  public Level: any = Level;

  constructor(@Optional() options?: Options) {
    // Move this to the constructor definition when optional parameters are working with @Injectable: https://github.com/angular/angular/issues/7344
    const { level, global, globalAs, store, storeAs } = Object.assign({}, DEFAULT_OPTIONS, options);

    this._level = level;
    this._globalAs = globalAs;
    this._storeAs = storeAs;

    if (global) {
      this.global();
    }

    if (store || this._loadLevel()) {
      this.store();
    }
  }

  private _loadLevel = (): Level => Number(localStorage.getItem(this._storeAs));

  private _storeLevel(level: Level): void {
    localStorage[this._storeAs] = level;
  }

  public error(_message?: any, ..._optionalParams: any[]): void {
    if (this.isErrorEnabled()) {
      console.error.apply(console, arguments);
    }
  }

  public warn(_message?: any, ..._optionalParams: any[]): void {
    if (this.isWarnEnabled()) {
      console.warn.apply(console, arguments);
    }
  }

  public info(message?: any, ...optionalParams: any[]): void {
    if (this.isInfoEnabled()) {
      /* tslint:disable:no-console */
      if (typeof console.info === 'function') {
        console.info.apply(console, arguments);
      } else {
        this.log(message, optionalParams);
      }
      /* tslint:enable:no-console */
    }
  }

  public debug(_message?: any, ..._optionalParams: any[]): void {
    if (this.isDebugEnabled()) {
      (console as any)[CONSOLE_DEBUG_METHOD].apply(console, arguments);
    }
  }

  public log(_message?: any, ..._optionalParams: any[]): void {
    if (this.isLogEnabled()) {
      console.log.apply(console, arguments);
    }
  }

  global = () => ((window as any)[this._globalAs] = this);

  public store(): Logger {
    this._store = true;
    const storedLevel = this._loadLevel();
    if (storedLevel) {
      this._level = storedLevel;
    } else {
      this._storeLevel(this.level);
    }

    return this;
  }

  public unstore(): Logger {
    this._store = false;
    localStorage.removeItem(this._storeAs);
    return this;
  }

  public isErrorEnabled = (): boolean => this.level >= Level.ERROR;
  public isWarnEnabled = (): boolean => this.level >= Level.WARN;
  public isInfoEnabled = (): boolean => this.level >= Level.INFO;
  public isDebugEnabled = (): boolean => this.level >= Level.DEBUG;
  public isLogEnabled = (): boolean => this.level >= Level.LOG;

  public get level(): Level {
    return this._level;
  }

  public set level(level: Level) {
    if (this._store) {
      this._storeLevel(level);
    }
    this._level = level;
  }
}
